import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from './Sidebar';
import { User, LogOut, Menu, Mail, Swords, X } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { on, isConnected } = useSocket();
    const [notifications, setNotifications] = useState<any[]>([]);

    useEffect(() => {
        const cleanup = on('new_challenge', (challenge: any) => {
            if (challenge.creatorId === user?.id) return;
            // Only notify if not on dashboard (where it's already shown)
            if (location.pathname === '/dashboard') return;
            
            setNotifications(prev => [challenge, ...prev].slice(0, 3));
            
            // Auto hide after 8 seconds
            setTimeout(() => {
                setNotifications(prev => prev.filter(c => c.id !== challenge.id));
            }, 8000);
        });
        return cleanup;
    }, [on, user?.id, location.pathname]);

    // Pages where we want to hide the footer and maximize space on mobile
    const isAppPage = ['/', '/dashboard', '/puzzles', '/analysis', '/openings', '/play', '/learn', '/profile', '/settings', '/admin'].includes(location.pathname);

    return (
        <div className="h-[100dvh] flex bg-[var(--bg-page)] text-[var(--text-main)] w-full overflow-hidden transition-colors duration-300">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 flex flex-col min-w-0 w-full relative">
                {/* Header */}
                <header className="h-[var(--header-h)] border-b border-[var(--border)] bg-[var(--bg-card)]/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 sticky top-0 z-[100] transition-colors duration-300 shrink-0">
                    <div className="flex items-center">
                        <button
                            className="p-2 -ml-2 mr-2 text-[var(--text-muted)] hover:text-white transition-colors grid place-items-center"
                            onClick={() => setIsSidebarOpen(true)}
                        >
                            <Menu size={24} />
                        </button>
                    </div>

                    <div className="flex items-center gap-4">
                        {user && (
                            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mr-2">
                                <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 animate-pulse'}`} />
                                <span className={`text-[8px] font-black uppercase tracking-widest ${isConnected ? 'text-green-500' : 'text-red-500'}`}>
                                    {isConnected ? 'LIVE' : 'RECONNECTING'}
                                </span>
                            </div>
                        )}

                        {user ? (
                            <div className="flex items-center gap-3">
                                <Link
                                    to="/profile"
                                    className="w-9 h-9 shrink-0 rounded-xl bg-white/5 border border-white/10 text-[var(--text-muted)] flex items-center justify-center hover:bg-[var(--primary)]/10 hover:border-[var(--primary)]/30 hover:text-[var(--primary)] transition-all"
                                    title="Mi Perfil"
                                >
                                    <User size={18} />
                                </Link>

                                <Link
                                    to="/contact"
                                    className="w-9 h-9 shrink-0 rounded-xl bg-white/5 border border-white/10 text-[var(--text-muted)] flex items-center justify-center hover:bg-white/10 hover:border-white/20 hover:text-white transition-all"
                                    title="Contacto"
                                >
                                    <Mail size={16} />
                                </Link>

                                <div className="flex items-center gap-2 pl-1">
                                    <button
                                        onClick={logout}
                                        className="w-9 h-9 shrink-0 rounded-xl bg-white/5 border border-white/10 text-[var(--text-muted)] flex items-center justify-center hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-all"
                                        title="Cerrar Sesión"
                                    >
                                        <LogOut size={16} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Link
                                    to="/login"
                                    className="text-[10px] lg:text-xs font-black tracking-widest px-4 py-2 rounded-xl border border-[var(--border)] text-[var(--text-muted)] hover:text-white hover:bg-white/5 transition-all uppercase"
                                >
                                    Login
                                </Link>
                                <Link
                                    to="/register"
                                    className="text-[10px] lg:text-xs font-black tracking-widest px-4 py-2 rounded-xl bg-[var(--primary)] text-white hover:bg-[var(--primary-bright)] shadow-lg shadow-[var(--primary-glow)] transition-all uppercase"
                                >
                                    Registro
                                </Link>
                            </div>
                        )}
                    </div>
                </header>

                {/* Main Content Area */}
                <main className={`flex-1 ${isAppPage ? 'overflow-hidden p-0 flex flex-col' : 'overflow-x-hidden p-4 md:p-10'}`}>
                    {children}
                </main>

                {/* Multiplayer Notifications Overlay */}
                {notifications.length > 0 && (
                    <div className="fixed bottom-6 right-6 z-[1000] flex flex-col gap-3 w-[300px] pointer-events-none">
                        {notifications.map(n => (
                            <div key={n.id} className="pointer-events-auto bg-[#1a1c24] border border-blue-500/30 rounded-2xl p-4 shadow-2xl shadow-blue-500/20 animate-in slide-in-from-right-4 fade-in duration-300">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <Swords size={14} className="text-blue-400" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white/40">Nuevo Reto Online</span>
                                    </div>
                                    <button 
                                        onClick={() => setNotifications(prev => prev.filter(c => c.id !== n.id))}
                                        className="text-white/20 hover:text-white"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                                <div className="text-xs font-bold text-white mb-3">
                                    {n.creator?.name} te reta a una partida de {n.timeControl || 'Ilimitada'}.
                                </div>
                                <button 
                                    onClick={() => {
                                        setNotifications([]);
                                        navigate('/dashboard');
                                    }}
                                    className="w-full py-2 rounded-xl bg-blue-500 text-white text-[10px] font-black uppercase tracking-widest hover:bg-blue-400 trasition-all"
                                >
                                    Aceptar Reto
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {(!isAppPage || window.innerWidth >= 1024) && (
                    <footer className="p-4 sm:p-8 text-center opacity-30 text-[0.7rem] font-medium tracking-widest shrink-0">
                        &copy; 2026 CHESS DREZ • PREMIUM CHESS PLATFORM
                    </footer>
                )}
            </div>
        </div>
    );
};

export default Layout;
